import React, { useState } from "react";
import { clinicFeedbackService, contactService } from "../../services/api";

/**
 * Hero FAQ + form "Sampaikan Kebutuhan Anda".
 * Dengan `slug` (halaman tenant /:slug/faq): kirim ke email klinik lewat API tenant.
 * Tanpa slug (mis. /careline-faq): kirim ke Qease lewat contact-messages (sama seperti landing).
 */
const CareLineFAQHero = ({ slug = null, primaryColor = "#06B6D4" }) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState("idle");
    const [errorText, setErrorText] = useState("");
    const [successText, setSuccessText] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorText("");
        setSuccessText("");
        setStatus("loading");
        try {
            if (slug) {
                const res = await clinicFeedbackService.submit(slug, {
                    slug,
                    name,
                    email,
                    message,
                });
                if (res.data?.success) {
                    setStatus("success");
                    setSuccessText(
                        res.data?.message ||
                            "Terima kasih. Pesan Anda telah diterima."
                    );
                    setName("");
                    setEmail("");
                    setMessage("");
                } else {
                    setStatus("error");
                    setErrorText(res.data?.message || "Gagal mengirim.");
                }
            } else {
                const res = await contactService.submit({ name, email, message });
                if (res.data?.success) {
                    setStatus("success");
                    setSuccessText(
                        res.data?.message ||
                            "Terima kasih. Pesan Anda telah diterima."
                    );
                    setName("");
                    setEmail("");
                    setMessage("");
                } else {
                    setStatus("error");
                    setErrorText("Gagal mengirim.");
                }
            }
        } catch (err) {
            setStatus("error");
            const msg =
                err.response?.data?.message ||
                (err.response?.data?.errors &&
                    Object.values(err.response.data.errors).flat().join(" ")) ||
                "Gagal mengirim. Periksa koneksi atau coba lagi.";
            setErrorText(msg);
        }
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
                @keyframes heroFadeUp {
                    from { opacity: 0; transform: translateY(28px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes formFadeUp {
                    from { opacity: 0; transform: translateY(24px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .hero-title { animation: heroFadeUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.1s both; }
                .hero-form  { animation: formFadeUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.25s both; }
            `}</style>

            <section
                className="relative w-full flex items-center overflow-hidden"
                style={{ minHeight: "clamp(560px, 70vw, 700px)" }}
            >
                <div
                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: "url('/careline/careline-faq.png')",
                    }}
                />
                <div className="absolute inset-0 z-0 bg-gradient-to-br from-cyan-400/20 to-slate-900/50" />

                <div className="absolute bottom-[-1px] left-0 w-full z-10 leading-none">
                    <svg
                        viewBox="0 0 1440 56"
                        preserveAspectRatio="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="block w-full h-14"
                    >
                        <path
                            d="M0,32 C360,56 1080,0 1440,32 L1440,56 L0,56 Z"
                            fill="#f8fafc"
                        />
                    </svg>
                </div>

                <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 flex flex-col lg:flex-row items-center lg:items-end justify-between gap-12">
                    <div className="hero-title text-center lg:text-left max-w-lg">
                        <h1
                            className="font-[Plus_Jakarta_Sans] font-extrabold text-white leading-[1.1] tracking-[-0.01em]"
                            style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}
                        >
                            Ada yang Ingin{" "}
                            <em className="not-italic text-transparent bg-clip-text bg-gradient-to-r from-[#a5f3fc] via-[#67e8f9] to-[#22d3ee]">
                                Ditanyakan?
                            </em>
                        </h1>
                        <p
                            className="font-[DM_Sans] font-light text-white/75 mt-4 max-w-sm mx-auto lg:mx-0"
                            style={{ fontSize: "clamp(0.875rem, 1.5vw, 1rem)" }}
                        >
                            Kami siap membantu Anda, baik untuk pertanyaan kecil
                            maupun kebutuhan layanan Anda.
                        </p>
                    </div>

                    <div className="hero-form w-full max-w-md">
                        <div className="bg-white rounded-3xl p-8 shadow-[0_24px_64px_rgba(15,23,42,0.18)] border border-slate-100">
                            <h2 className="font-[Plus_Jakarta_Sans] font-bold text-slate-900 text-lg mb-1">
                                Sampaikan Kebutuhan Anda
                            </h2>
                            <p className="font-[DM_Sans] text-slate-400 text-xs leading-relaxed mb-6">
                                Isi formulir di bawah dan tim kami akan segera
                                menghubungi Anda.
                            </p>

                            {status === "success" && successText && (
                                <div className="mb-4 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-[DM_Sans] text-emerald-800">
                                    {successText}
                                </div>
                            )}
                            {status === "error" && errorText && (
                                <div className="mb-4 rounded-xl bg-red-50 px-3 py-2 text-xs font-[DM_Sans] text-red-800">
                                    {errorText}
                                </div>
                            )}

                            <form className="space-y-4" onSubmit={handleSubmit}>
                                <div className="space-y-1">
                                    <label className="font-[DM_Sans] text-xs font-semibold text-slate-700 ml-1">
                                        Nama
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                            placeholder="Nama Anda"
                                            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-xs font-[DM_Sans] focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-all placeholder:text-slate-300"
                                        />
                                        <svg
                                            className="absolute left-3 top-2.5 text-slate-300 w-4 h-4"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                            <circle cx="12" cy="7" r="4" />
                                        </svg>
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="font-[DM_Sans] text-xs font-semibold text-slate-700 ml-1">
                                        Email
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            placeholder="Email Anda"
                                            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-xs font-[DM_Sans] focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-all placeholder:text-slate-300"
                                        />
                                        <svg
                                            className="absolute left-3 top-2.5 text-slate-300 w-4 h-4"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                            <polyline points="22,6 12,13 2,6" />
                                        </svg>
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="font-[DM_Sans] text-xs font-semibold text-slate-700 ml-1">
                                        Pesan
                                    </label>
                                    <textarea
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        required
                                        rows={4}
                                        placeholder="Tuliskan pesan Anda di sini"
                                        className="w-full p-4 rounded-xl border border-slate-200 text-xs font-[DM_Sans] focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-all placeholder:text-slate-300 resize-none"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={status === "loading"}
                                    className="w-full py-3 text-white font-[Plus_Jakarta_Sans] font-bold text-sm rounded-xl transition-all duration-200 shadow-lg shadow-cyan-500/20 hover:brightness-105 active:scale-[0.98] mt-1 disabled:opacity-60 disabled:cursor-not-allowed"
                                    style={{
                                        background: `linear-gradient(to right, ${primaryColor}, #0891B2)`,
                                    }}
                                >
                                    {status === "loading"
                                        ? "Mengirim…"
                                        : "Kirim Pesan"}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default CareLineFAQHero;
